import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { initPushNotifications } from "../utils/pushNotifications";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("tribe_token"));
  const [needsUsername, setNeedsUsername] = useState(false);
  const [needsAadhaar, setNeedsAadhaar] = useState(false);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const [otpEnabled, setOtpEnabled] = useState(false);

  const checkSubscription = async (authToken) => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSubscriptionActive(res.data.is_active);
    } catch (error) {
      setSubscriptionActive(true);
    }
  };

  const checkAadhaarStatus = (userData) => {
    return userData.country === "India" && !userData.aadhaar_verified;
  };

  useEffect(() => {
    const initAuth = async () => {
      // Fetch login mode
      try {
        const modeRes = await axios.get(`${API}/auth/login-mode`);
        setOtpEnabled(modeRes.data.otp_enabled);
      } catch { /* default to false = password mode */ }

      // Handle Emergent Google Auth callback (session_id in URL fragment)
      try {
        const hash = window.location.hash || "";
        if (hash.includes("session_id=")) {
          const params = new URLSearchParams(hash.replace(/^#/, ""));
          const sid = params.get("session_id");
          if (sid) {
            // Clean URL fragment immediately
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
            try {
              const res = await axios.post(`${API}/auth/google-callback`, { session_id: sid });
              const { access_token, user: userData, is_new } = res.data;
              localStorage.setItem("tribe_token", access_token);
              setToken(access_token);
              setUser(userData);
              setNeedsUsername(!userData.username);
              setNeedsAadhaar(checkAadhaarStatus(userData));
              setNeedsProfileCompletion(userData.username && !userData.profile_completed);
              if (userData.username) {
                await checkSubscription(access_token);
              }
              initPushNotifications(access_token);
              if (is_new) {
                localStorage.setItem("aixtribe_show_welcome", "true");
                window.dispatchEvent(new Event("aixtribe_welcome"));
              }
              setLoading(false);
              return;
            } catch (e) {
              console.error("Google auth callback failed:", e);
            }
          }
        }
      } catch { /* ignore */ }

      const savedToken = localStorage.getItem("tribe_token");
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` },
          });
          setUser(response.data);
          setToken(savedToken);
          setNeedsUsername(!response.data.username);
          setNeedsAadhaar(checkAadhaarStatus(response.data));
          setNeedsProfileCompletion(response.data.username && !response.data.profile_completed);
          if (response.data.username) {
            await checkSubscription(savedToken);
          }
          initPushNotifications(savedToken);
        } catch (error) {
          localStorage.removeItem("tribe_token");
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const requestOTP = async (email) => {
    const response = await axios.post(`${API}/auth/request-otp`, { email });
    return response.data;
  };

  const verifyOTP = async (email, otp) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true, temp_token: response.data.temp_token };
    }
    const { access_token, user: userData, needs_username } = response.data;
    localStorage.setItem("tribe_token", access_token);
    setToken(access_token);
    setUser(userData);
    setNeedsUsername(needs_username);
    setNeedsAadhaar(checkAadhaarStatus(userData));
    setNeedsProfileCompletion(userData.username && !userData.profile_completed);
    if (!needs_username && userData.username) {
      await checkSubscription(access_token);
    }
    return { user: userData, needs_username, needs_aadhaar: checkAadhaarStatus(userData) };
  };

  const loginWithPassword = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.requires_2fa) {
      return { requires_2fa: true, temp_token: response.data.temp_token };
    }
    const { access_token, user: userData, needs_username } = response.data;
    localStorage.setItem("tribe_token", access_token);
    setToken(access_token);
    setUser(userData);
    setNeedsUsername(needs_username);
    setNeedsAadhaar(checkAadhaarStatus(userData));
    setNeedsProfileCompletion(userData.username && !userData.profile_completed);
    if (!needs_username && userData.username) {
      await checkSubscription(access_token);
    }
    initPushNotifications(access_token);
    return { user: userData, needs_username };
  };

  const registerWithPassword = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("tribe_token", access_token);
    localStorage.setItem("aixtribe_show_welcome", "true");
    setToken(access_token);
    setUser(userData);
    setNeedsUsername(false);
    setNeedsProfileCompletion(userData.username && !userData.profile_completed);
    await checkSubscription(access_token);
    initPushNotifications(access_token);
    window.dispatchEvent(new Event("aixtribe_welcome"));
    return { user: userData };
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }, { headers: { Authorization: `Bearer ${token}` } });
    return response.data;
  };

  const verify2FA = async (tempToken, code) => {
    const response = await axios.post(`${API}/auth/2fa/validate`, { temp_token: tempToken, code });
    const { access_token, user: userData, needs_username } = response.data;
    localStorage.setItem("tribe_token", access_token);
    setToken(access_token);
    setUser(userData);
    setNeedsUsername(needs_username);
    setNeedsAadhaar(checkAadhaarStatus(userData));
    setNeedsProfileCompletion(userData.username && !userData.profile_completed);
    if (!needs_username && userData.username) {
      await checkSubscription(access_token);
    }
    return { user: userData, needs_username, needs_aadhaar: checkAadhaarStatus(userData) };
  };

  const setupUsername = async (username, country, name, mobile) => {
    const response = await axios.post(
      `${API}/auth/setup-username`,
      { username, country, name: name || username, mobile: mobile || "" },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(response.data.user);
    setNeedsUsername(false);
    const aadhaarNeeded = response.data.needs_aadhaar || false;
    setNeedsAadhaar(aadhaarNeeded);
    if (!aadhaarNeeded) {
      await checkSubscription(token);
    }
    return { ...response.data, needs_aadhaar: aadhaarNeeded };
  };

  const sendAadhaarOTP = async (aadhaarNumber) => {
    const response = await axios.post(
      `${API}/auth/aadhaar/send-otp`,
      { aadhaar_number: aadhaarNumber },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const verifyAadhaarOTP = async (otp) => {
    const response = await axios.post(
      `${API}/auth/aadhaar/verify-otp`,
      { otp },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNeedsAadhaar(false);
    setUser(prev => ({ ...prev, aadhaar_verified: true }));
    await checkSubscription(token);
    return response.data;
  };

  const checkUsername = async (username) => {
    const response = await axios.post(`${API}/auth/check-username`, { username });
    return response.data;
  };

  const updateProfile = async (profileData) => {
    const response = await axios.put(
      `${API}/users/profile`,
      profileData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(response.data);
    setNeedsProfileCompletion(!response.data.profile_completed);
    return response.data;
  };

  const deleteAccount = async () => {
    await axios.delete(`${API}/auth/delete-account`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    logout();
  };

  const logout = () => {
    localStorage.removeItem("tribe_token");
    setToken(null);
    setUser(null);
    setNeedsUsername(false);
    setNeedsAadhaar(false);
    setNeedsProfileCompletion(false);
    setSubscriptionActive(null);
  };

  const getAuthHeaders = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const refreshUser = async () => {
    if (token) {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      setNeedsAadhaar(checkAadhaarStatus(response.data));
      await checkSubscription(token);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        needsUsername,
        needsAadhaar,
        needsProfileCompletion,
        subscriptionActive,
        otpEnabled,
        requestOTP,
        verifyOTP,
        verify2FA,
        loginWithPassword,
        registerWithPassword,
        changePassword,
        setupUsername,
        checkUsername,
        sendAadhaarOTP,
        verifyAadhaarOTP,
        updateProfile,
        deleteAccount,
        logout,
        getAuthHeaders,
        refreshUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
